export interface DefenseEvents {
  "state:changed": undefined;
  "navigation:changed": { revision: number };
  "tower:selected": { towerId: string | null };
  "build:selected": { towerDefinitionId: string | null };
  "placement:feedback": { valid: boolean; message: string };
  "wave:started": { wave: number };
  "wave:completed": { wave: number };
  "draft:offered": { abilityIds: readonly string[] };
  "draft:selected": { abilityId: string; towerId: string };
  "enemy:spawned": { enemyId: string; durationMs: number };
  "enemy:activated": { enemyId: string };
  "enemy:hit": { enemyId: string; amount: number; type: import("../domain/types").DamageType };
  "enemy:escaped": { enemyId: string; livesLost: number; durationMs: number };
  "enemy:destroyed": {
    enemyId: string;
    reward: number;
    type: import("../domain/types").DamageType;
    durationMs: number;
  };
  "enemy:removed": { enemyId: string };
  "tower:placed": { towerId: string };
  "tower:upgraded": { towerId: string; level: number };
  "tower:sold": { towerId: string };
  "combat:fired": { towerId: string; towerDefinitionId: string; enemyIds: readonly string[] };
  "game:ended": { victory: boolean };
  "game:restart-requested": undefined;
  "game:exit-requested": undefined;
  "settings:changed": undefined;
}

type Listener<T> = (payload: T) => void;

export class TypedEventBus {
  private readonly listeners = new Map<keyof DefenseEvents, Set<Listener<never>>>();

  on<K extends keyof DefenseEvents>(event: K, listener: Listener<DefenseEvents[K]>): () => void {
    let bucket = this.listeners.get(event);
    if (!bucket) {
      bucket = new Set();
      this.listeners.set(event, bucket);
    }
    bucket.add(listener as Listener<never>);
    return () => bucket?.delete(listener as Listener<never>);
  }

  emit<K extends keyof DefenseEvents>(event: K, payload: DefenseEvents[K]): void {
    for (const listener of this.listeners.get(event) ?? []) listener(payload as never);
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const events = new TypedEventBus();
